import { api } from '../api';

// ── Types ─────────────────────────────────────────────────────────────

export type SubscriptionInfo = {
  id: string;
  org_id: string;
  stripe_subscription_id: string | null;
  plan: string;
  billing_cycle: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  trial_end: string | null;
  usage_overage_enabled: boolean;
  created_at: string;
  updated_at: string;
};

export type PlanInfo = {
  plan: string;
  max_members: number;
  max_projects: number;
  monthly_token_limit: number;
  features: Record<string, boolean>;
};

export type UsageSummary = {
  total_tokens_used: number;
  total_tokens_limit: number;
  total_overage: number;
};

export type BillingStatus = {
  subscription: SubscriptionInfo | null;
  plan_info: PlanInfo;
  usage: UsageSummary;
  billing_enabled: boolean;
  stripe_publishable_key: string | null;
};

export type InvoiceInfo = {
  id: string;
  org_id: string;
  stripe_invoice_id: string | null;
  amount_cents: number;
  currency: string;
  status: string;
  period_start: string | null;
  period_end: string | null;
  pdf_url: string | null;
  created_at: string;
};

// ── API calls ─────────────────────────────────────────────────────────

export const fetchBillingStatus = async (): Promise<BillingStatus> =>
  api.get('api/billing').json();

export const createCheckoutSession = async (data: {
  plan: 'pro' | 'elite';
  billing_cycle: 'monthly' | 'yearly';
  success_url: string;
  cancel_url: string;
}): Promise<{ checkout_url: string; session_id: string }> =>
  api.post('api/billing/checkout', { json: data }).json();

export const createPortalSession = async (data: {
  return_url: string;
}): Promise<{ portal_url: string }> =>
  api.post('api/billing/portal', { json: data }).json();

export const fetchInvoices = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<{ invoices: InvoiceInfo[] }> => {
  const searchParams: Record<string, string> = {};
  if (params?.limit) searchParams.limit = String(params.limit);
  if (params?.offset) searchParams.offset = String(params.offset);
  return api.get('api/billing/invoices', { searchParams }).json();
};

export const updateOverage = async (data: {
  usage_overage_enabled: boolean;
}): Promise<{ ok: boolean }> =>
  api.put('api/billing/overage', { json: data }).json();
